/**
 * ItemConnector Component
 * 
 * Componente para criar conexões visuais entre itens da requisição e itens extraídos.
 * Desenha linhas SVG conectando os cards correspondentes.
 */

import React, { useEffect, useState, useMemo } from 'react';
import './ItemConnector.css';

const ItemConnector = ({ 
  fromRef, 
  toRef, 
  color = '#6366f1', 
  strokeWidth = 2,
  isActive = true 
}) => {
  const [path, setPath] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const markerId = useMemo(() => `arrowhead-${color.replace('#', '')}-${Math.random().toString(36).substr(2, 9)}`, [color]);

  useEffect(() => {
    const updatePath = () => {
      if (!fromRef?.current || !toRef?.current) {
        setIsVisible(false);
        return;
      }

      const fromRect = fromRef.current.getBoundingClientRect();
      const toRect = toRef.current.getBoundingClientRect();
      const containerRect = fromRef.current.closest('.mirror-verification-split')?.getBoundingClientRect();

      if (!containerRect) {
        setIsVisible(false);
        return;
      }

      // Calcular posições relativas ao container
      const fromX = fromRect.right - containerRect.left;
      const fromY = fromRect.top + fromRect.height / 2 - containerRect.top;
      const toX = toRect.left - containerRect.left;
      const toY = toRect.top + toRect.height / 2 - containerRect.top;

      // Criar curva suave (bezier)
      const controlPointOffset = Math.abs(toX - fromX) * 0.3;

      const pathData = `M ${fromX} ${fromY} 
        C ${fromX + controlPointOffset} ${fromY}, 
          ${toX - controlPointOffset} ${toY}, 
          ${toX} ${toY}`;

      setPath(pathData);
      setIsVisible(true);
    };

    // Delay inicial para garantir que os elementos estão renderizados
    const timeoutId = setTimeout(updatePath, 100);
    updatePath();

    // Atualizar quando a janela redimensionar ou scroll
    window.addEventListener('resize', updatePath);
    window.addEventListener('scroll', updatePath, true);

    // Observer para mudanças no DOM
    const observer = new ResizeObserver(updatePath);
    if (fromRef?.current) observer.observe(fromRef.current);
    if (toRef?.current) observer.observe(toRef.current);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updatePath);
      window.removeEventListener('scroll', updatePath, true);
      observer.disconnect();
    };
  }, [fromRef, toRef]);

  if (!isVisible || !path) return null;

  return (
    <svg
      className="item-connector-svg"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 1,
        overflow: 'visible'
      }}
    >
      <defs>
        <marker
          id={markerId}
          markerWidth="10"
          markerHeight="10"
          refX="9"
          refY="3"
          orient="auto"
        >
          <polygon
            points="0 0, 10 3, 0 6"
            fill={color}
            opacity={isActive ? 0.7 : 0.3}
          />
        </marker>
      </defs>
      <path
        d={path}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeOpacity={isActive ? 0.7 : 0.3}
        markerEnd={`url(#${markerId})`}
        className="item-connector-path"
      />
    </svg>
  );
};

export default ItemConnector;
